/**
 * KAN-865: панель карточки клиента с канбан-доски CRM: поля строки CRM
 * (правка как в таблице) и действия строки (useCrmRowActions, общие с
 * таблицей). Данные: GET /api/v1/crm/clients/<id>/ (useCrmClientRow).
 * desktop: панель справа ~420px; mobile: снизу на 90% экрана.
 * clientId null: панель закрыта, запроса нет. "Закрыть" и Esc: onClose.
 * KAN-880: Esc в раскрытом выборе "Отработано" закрывает только выбор.
 */
import { Link } from 'react-router-dom';
import { Loader2, UserRound } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { isNotFoundError } from '@/hooks/useCatalog';
import { useCrmClientRow } from '@/hooks/useCrmClientRow';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { cn } from '@/lib/utils';
import { isInsideOpenWorkOutcome, type WorkSurface } from '@/lib/workActions';
import { useCrmRowActions } from '../CrmRowActions';
import { CrmClientDetails } from './CrmClientDetails';

export interface CrmClientPanelProps {
  clientId: string | null;
  variant: 'desktop' | 'mobile';
  onClose: () => void;
  /** Ссылка "Карточка клиента"; по умолчанию /clients/<id>. */
  clientHref?: (clientId: string) => string;
  /** Поверхность действий "Взять в работу" (KAN-852): кабинет или приложение менеджера. */
  surface?: WorkSurface;
}

const defaultClientHref = (clientId: string) => `/clients/${encodeURIComponent(clientId)}`;

const VARIANT_CLASS = {
  desktop: 'w-full overflow-y-auto sm:max-w-[420px]',
  mobile: 'h-[90dvh] overflow-y-auto rounded-t-2xl pb-[calc(1.5rem+env(safe-area-inset-bottom))]',
} as const;

/** Esc внутри поля ввода закрывает редактор ячейки, а не панель. */
function inEditor(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest('input,textarea,select');
}

export function CrmClientPanel({
  clientId,
  variant,
  onClose,
  clientHref = defaultClientHref,
  surface = 'cabinet',
}: CrmClientPanelProps): JSX.Element {
  const client = useCrmClientRow(clientId);
  const rowActions = useCrmRowActions(client.queryKey, surface);
  const row = client.item?.row;
  const name = row?.contact_name?.trim() || row?.phone?.trim() || 'Клиент';

  let body: JSX.Element;
  if (client.item && client.data) {
    body = (
      <CrmClientDetails
        item={client.item}
        stages={client.data.stages ?? []}
        attributes={client.data.attributes ?? []}
        actions={rowActions.render(client.item, { compact: false })}
        callSize={variant === 'mobile' ? 'lg' : 'sm'}
      />
    );
  } else if (client.isLoading) {
    body = (
      <div className="flex justify-center py-10" data-testid="crm-client-panel-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-label="Загрузка" />
      </div>
    );
  } else {
    const text = isNotFoundError(client.error)
      ? 'Клиент не найден или недоступен.'
      : `Не удалось загрузить клиента: ${extractErrorMessage(client.error, 'ошибка сервера')}`;
    body = <p className="py-6 text-sm text-red-600">{text}</p>;
  }

  return (
    <Sheet open={clientId !== null} onOpenChange={(open) => !open && onClose()}>
      {clientId !== null && (
        <SheetContent
          side={variant === 'mobile' ? 'bottom' : 'right'}
          data-testid="crm-client-panel"
          data-variant={variant}
          className={cn('flex flex-col gap-4', VARIANT_CLASS[variant])}
          onEscapeKeyDown={(event) => {
            // Esc в редакторе ячейки или в раскрытом выборе "Отработано" закрывает их, а не панель.
            if (inEditor(event.target) || isInsideOpenWorkOutcome(event.target)) event.preventDefault();
          }}
        >
          <SheetHeader className="space-y-1 pr-8 text-left">
            <SheetTitle className="truncate">{name}</SheetTitle>
            <SheetDescription asChild>
              <Link
                to={clientHref(clientId)}
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"
              >
                <UserRound className="h-4 w-4" aria-hidden="true" />
                Карточка клиента
              </Link>
            </SheetDescription>
          </SheetHeader>
          {body}
          {rowActions.dialogs}
        </SheetContent>
      )}
    </Sheet>
  );
}
